/**
 * <scroll-carousel>
 *
 * Progressive enhancement for the shared homepage carousel. The track scrolls
 * natively with scroll-snap — touch and trackpad work with no JavaScript at all.
 * This element adds these on top:
 *
 *   1. pointer drag, so a mouse can grab and throw the track (native
 *      overflow-x:auto gives no click-and-drag)
 *   2. the progress bar / dots / counter, reflected from real scroll position,
 *      with the bar draggable like a scrollbar
 *   3. the optional previous / next buttons
 *   4. an optional marquee: continuous scrolling that never settles, off unless a
 *      section asks for it
 */
if (!customElements.get('scroll-carousel')) {
  customElements.define(
    'scroll-carousel',
    class ScrollCarousel extends HTMLElement {
      /* A gesture that moves further than this counts as a drag, not a click. */
      static DRAG_THRESHOLD = 5;

      constructor() {
        super();
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.scrollPage = this.scrollPage.bind(this);
        this.onDotClick = this.onDotClick.bind(this);
        this.tickMarquee = this.tickMarquee.bind(this);
        this.startMarquee = this.startMarquee.bind(this);
        this.pauseMarquee = this.pauseMarquee.bind(this);
        this.holdMarquee = this.holdMarquee.bind(this);
        this.releaseMarquee = this.releaseMarquee.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);
        this.onDragStart = this.onDragStart.bind(this);
        this.onRefresh = this.onRefresh.bind(this);
        this.onBarPointerDown = this.onBarPointerDown.bind(this);
        this.onBarPointerMove = this.onBarPointerMove.bind(this);
        this.onBarPointerUp = this.onBarPointerUp.bind(this);

        this.barDrag = null;
        this.dragging = false;
        this.pendingDrag = false;
        this.suppressClick = false;
        this.dragDistance = 0;
      }

      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;

        /* A marquee is driven by MarqueeController below, outside this
           customElements.define guard. Binding drag and dots here would steal
           clicks and write scrollLeft against a strip that moves by transform. */
        if (this.classList.contains('scroll-carousel--marquee')) return;

        this.setupLoop();

        this.bar = this.querySelector('[data-carousel-bar]');
        this.thumb = this.querySelector('[data-carousel-thumb]');
        this.shown = this.querySelector('[data-carousel-shown]');
        this.totalOutput = this.querySelector('[data-carousel-total]');
        this.dots = Array.from(this.querySelectorAll('.scroll-carousel__dot'));
        this.prevButton = this.querySelector('[data-carousel-prev]');
        this.nextButton = this.querySelector('[data-carousel-next]');

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prevButton?.addEventListener('click', this.scrollPage);
        this.nextButton?.addEventListener('click', this.scrollPage);
        for (const dot of this.dots) dot.addEventListener('click', this.onDotClick);

        /* passive: none of these call preventDefault - only onDragStart and
           onClickCapture do, and those are dragstart and click. Without the
           flag Safari cannot know that, so on iOS it holds the touch scroll
           until the listener has run before it will move the track, which is
           felt as a stiff, laggy drag. The handlers already bail out
           immediately for pointerType touch, so they do no work there. */
        this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.track.addEventListener('pointermove', this.onPointerMove, { passive: true });
        this.track.addEventListener('pointerup', this.onPointerUp, { passive: true });
        this.track.addEventListener('pointercancel', this.onPointerUp, { passive: true });
        /* Capture phase: a drag must not also activate the link underneath. */
        this.track.addEventListener('click', this.onClickCapture, true);
        this.track.addEventListener('dragstart', this.onDragStart);

        this.bar?.addEventListener('pointerdown', this.onBarPointerDown);
        this.bar?.addEventListener('pointermove', this.onBarPointerMove);
        this.bar?.addEventListener('pointerup', this.onBarPointerUp);
        this.bar?.addEventListener('pointercancel', this.onBarPointerUp);

        /* Sections that show and hide slides (league filters, tabs) fire this
           so the progress readout re-measures without knowing our internals.
           detail.reset also asks for a return to the first slide. */
        this.addEventListener('carousel:refresh', this.onRefresh);

        /* ResizeObserver only fires when the track box itself changes. Images
           finishing load change scrollWidth but not the track size, which would
           otherwise leave the thumb width and grab cursor stale. */
        this.track.addEventListener('load', this.onResize, true);
        window.addEventListener('load', this.onResize);

        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
        for (const slide of this.slides) this.resizeObserver.observe(slide);

        this.update();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.prevButton?.removeEventListener('click', this.scrollPage);
        this.nextButton?.removeEventListener('click', this.scrollPage);
        for (const dot of this.dots || []) dot.removeEventListener('click', this.onDotClick);
        this.track?.removeEventListener('pointerdown', this.onPointerDown);
        this.track?.removeEventListener('pointermove', this.onPointerMove);
        this.track?.removeEventListener('pointerup', this.onPointerUp);
        this.track?.removeEventListener('pointercancel', this.onPointerUp);
        this.track?.removeEventListener('click', this.onClickCapture, true);
        this.track?.removeEventListener('dragstart', this.onDragStart);
        this.bar?.removeEventListener('pointerdown', this.onBarPointerDown);
        this.bar?.removeEventListener('pointermove', this.onBarPointerMove);
        this.bar?.removeEventListener('pointerup', this.onBarPointerUp);
        this.bar?.removeEventListener('pointercancel', this.onBarPointerUp);
        this.removeEventListener('carousel:refresh', this.onRefresh);
        this.resizeObserver?.disconnect();
        this.pauseMarquee();
        clearTimeout(this.goToTimer);
        cancelAnimationFrame(this.frame);
        cancelAnimationFrame(this.settleFrame);
        clearTimeout(this.settleTimer);
        cancelAnimationFrame(this.restoreFrame);
        clearTimeout(this.restoreTimer);
      }

      /* Clones are scenery. Excluding them here keeps every count downstream —
         the counter total, the slide step, the dots — measuring real slides. */
      get slides() {
        return Array.from(this.track?.children || []).filter((slide) => !slide.hasAttribute('data-carousel-clone'));
      }

      /* A filtered-out slide is display:none, so it has no box. Counting boxes
         rather than elements is what makes the readout follow the filter. */
      get visibleSlides() {
        return this.slides.filter((slide) => slide.offsetWidth > 0 || slide.offsetHeight > 0);
      }

      /* Without data-total-auto the merchant typed an explicit total ("6 / 120
         teams"), which must be shown verbatim. */
      get total() {
        if (this.hasAttribute('data-total-auto')) return this.visibleSlides.length;
        return parseInt(this.dataset.total, 10) || this.visibleSlides.length;
      }

      get scrollable() {
        return this.track.scrollWidth - this.track.clientWidth > 1;
      }

      /* True when the track has room for the last slide to scroll to the
         leading edge, so every slide is its own position and gets its own dot.
         A row showing several slides at once cannot do that and keeps one dot
         per page. */
      get everySlideReachesStart() {
        const step = this.slideStep();
        const max = this.track.scrollWidth - this.track.clientWidth;
        return step > 0 && this.dots.length > 1 && max >= (this.dots.length - 1) * step - 1;
      }

      /* ----- seamless loop ----- */

      get loops() {
        if (this.marqueeDuration > 0) {
          return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }

        return this.hasAttribute('data-loop');
      }

      /* One full set of slides, gap included: the distance the track travels
         before the clones put identical content back under the viewport. Measured
         from the DOM rather than summed, so it needs no knowledge of the widths a
         consuming section set. */
      get loopWidth() {
        const [first] = this.slides;
        const clone = this.track.querySelector('[data-carousel-clone]');
        if (!first || !clone) return 0;

        return clone.offsetLeft - first.offsetLeft;
      }

      /* A copy of the slides appended after the originals, so there is always
         another slide to move onto. The copies are hidden from assistive tech and
         taken out of the tab order — they are the same content twice, and a
         keyboard would otherwise walk the whole strip again.
         ponytail: this is why `loop` is documented as being for slides that hold
         no interactive content. Untabbable is not unclickable: a cloned link is
         still a link to a mouse, and a cloned add-to-cart button would submit.
         The gallery's slides are bare images, which is what makes it safe here.
         Cloning a carousel of product cards needs the clones inert, e.g. behind
         `inert` on the clone, before it would be honest. */
      setupLoop() {
        if (!this.loops) return;
        if (this.track.querySelector('[data-carousel-clone]')) return;
        /* A single slide has nothing to loop through, and its clone would not be
           safely offscreen: a viewport showing 1.694 slides would put the same
           photo on screen twice, side by side. */
        if (this.slides.length < 2) return;

        const clones = this.slides.map((slide) => {
          const clone = slide.cloneNode(true);
          clone.setAttribute('data-carousel-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          for (const node of clone.querySelectorAll('a, button, input, select, textarea, [tabindex]')) {
            node.setAttribute('tabindex', '-1');
          }
          return clone;
        });

        this.track.append(...clones);
      }

      /* ----- continuous marquee ----- */

      /* Milliseconds for one slide to travel past. 0 means the track is manual. */
      get marqueeDuration() {
        return parseInt(this.dataset.marquee, 10) || 0;
      }

      startMarquee() {
        if (!this.marqueeDuration || this.marqueeHeld) return;
        if (this.slides.length < 2) return;
        if (document.hidden) return;

        cancelAnimationFrame(this.marqueeFrame);
        this.marqueeLastTime = undefined;
        this.marqueeFrame = requestAnimationFrame(this.tickMarquee);
      }

      pauseMarquee() {
        cancelAnimationFrame(this.marqueeFrame);
        this.marqueeFrame = undefined;
        this.marqueeLastTime = undefined;
      }

      /* Hover, focus, drag and a hidden tab all call this so they share one
         counter. startMarquee is a no-op while the hold is above zero. */
      holdMarquee() {
        this.marqueeHeld = (this.marqueeHeld || 0) + 1;
        this.pauseMarquee();
      }

      releaseMarquee() {
        this.marqueeHeld = Math.max((this.marqueeHeld || 0) - 1, 0);
        if (this.marqueeHeld === 0) this.startMarquee();
      }

      onVisibilityChange() {
        if (document.hidden) {
          this.pauseMarquee();
          return;
        }

        this.startMarquee();
      }

      tickMarquee(now) {
        const last = this.marqueeLastTime ?? now;
        /* A background tab that missed the visibility listener can dump a huge
           delta on return; cap it so the strip does not jump a whole slide. */
        const delta = Math.min(now - last, 48);
        this.marqueeLastTime = now;

        const step = this.slideStep();
        const duration = this.marqueeDuration;

        if (step > 0 && duration > 0 && !this.dragging) {
          this.track.scrollLeft += (step / duration) * delta;
        }

        this.marqueeFrame = requestAnimationFrame(this.tickMarquee);
      }

      /* ----- pointer drag ----- */

      onPointerDown(event) {
        /* A new gesture: whatever the last one decided about swallowing a click
           no longer applies. */
        this.suppressClick = false;

        /* Pause the marquee for the whole press, including touch, so the two
           do not fight. Released in onPointerUp. */
        this.holdMarquee();
        this.pointerHeld = true;

        /* Touch already scrolls natively, with momentum we cannot better — but
           the marquee hold above still applies. A mouse gets the drag path. */
        if (event.pointerType === 'touch') return;
        if (event.button !== 0) return;
        if (!this.scrollable) return;

        /* Deliberately does NOT start the drag or capture the pointer yet.
           setPointerCapture retargets the following click to the capturing
           element, so claiming the pointer here stole every click from the
           controls inside a slide — the fan gallery's play button among them,
           which is why it responded to the keyboard but not the mouse. It also
           put the track in drag mode on a plain press. Both wait for movement
           past the threshold; see onPointerMove. */
        this.pendingDrag = true;
        this.pointerId = event.pointerId;
        this.dragDistance = 0;
        this.dragOriginX = event.clientX;
        this.dragOriginScroll = this.track.scrollLeft;
      }

      onPointerMove(event) {
        if (!this.pendingDrag && !this.dragging) return;
        if (event.pointerId !== this.pointerId) return;

        const delta = event.clientX - this.dragOriginX;
        this.dragDistance = Math.max(this.dragDistance, Math.abs(delta));

        /* This is the point where a press becomes a drag. */
        if (!this.dragging) {
          if (this.dragDistance <= ScrollCarousel.DRAG_THRESHOLD) return;
          this.dragging = true;
          this.track.setPointerCapture?.(event.pointerId);
          this.track.classList.add('is-dragging');
        }

        this.track.scrollLeft = this.dragOriginScroll - delta;
      }

      onPointerUp(event) {
        if (this.pointerHeld) {
          this.pointerHeld = false;
          this.releaseMarquee();
        }

        this.pendingDrag = false;
        if (!this.dragging) return;

        this.dragging = false;
        /* Only a gesture that actually dragged should eat the click that
           follows it. Tying this to the completed drag rather than to a lingering
           distance keeps a later click from being swallowed. */
        this.suppressClick = this.dragDistance > ScrollCarousel.DRAG_THRESHOLD;
        this.track.releasePointerCapture?.(event.pointerId);
        /* Decide the landing slide before snap comes back. With one slide per
           view, mandatory snap waits until the drag has passed halfway — on
           team-feature that is most of the card, so a normal drag springs
           back and only a pull that carries the slide out of the card sticks. */
        this.settleDrag();
        /* Dropping the class restores scroll-snap on a manual carousel. A
           marquee keeps snap off via .is-marquee so it does not settle. */
        this.track.classList.remove('is-dragging');
      }

      /* A drag that clearly picked a direction moves one slide that way, even
         when it stopped short of the next snap point. A drag that already
         travelled whole slides keeps every one of them. A fidget returns to
         the slide the gesture started on. */
      settleDrag() {
        const step = this.slideStep();
        if (step <= 0) return;

        const max = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
        const traveled = this.track.scrollLeft - this.dragOriginScroll;
        const startIndex = Math.round(this.dragOriginScroll / step);
        const pages = Math.trunc(traveled / step);
        const remainder = traveled - pages * step;
        const commit = Math.min(48, step * 0.2);

        let steps = pages;
        if (Math.abs(remainder) >= commit) steps += Math.sign(remainder);

        let target = (startIndex + steps) * step;
        if (target > max) target = max;
        if (target < 0) target = 0;

        this.scrollToOffset(target);
      }

      onClickCapture(event) {
        if (!this.suppressClick) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }

      onDragStart(event) {
        /* Stops the browser's native image/link drag fighting the gesture. */
        event.preventDefault();
      }

      /* ----- draggable bar ----- */

      /* Pressed on the thumb, the point under the pointer stays put; pressed on
         the empty bar, the thumb centres on the pointer and the same press
         carries on as a drag. This only moves the track — onScroll still draws
         the thumb, so the bar and the track can never disagree. */
      onBarPointerDown(event) {
        if (event.button !== 0) return;
        if (!this.thumb || !this.scrollable) return;

        /* No text selection or native image drag under a moving pointer. */
        event.preventDefault();

        const barBox = this.bar.getBoundingClientRect();
        const thumbBox = this.thumb.getBoundingClientRect();
        const onThumb = event.clientX >= thumbBox.left && event.clientX <= thumbBox.right;

        this.barDrag = {
          pointerId: event.pointerId,
          barLeft: barBox.left,
          travel: barBox.width - thumbBox.width,
          grab: onThumb ? event.clientX - thumbBox.left : thumbBox.width / 2,
        };

        this.bar.setPointerCapture?.(event.pointerId);
        this.bar.classList.add('is-dragging');
        /* Snapping would fight every scrollLeft write; onBarPointerUp hands it back. */
        this.track.style.scrollSnapType = 'none';
        this.scrollToBarPointer(event.clientX);
      }

      onBarPointerMove(event) {
        if (event.pointerId !== this.barDrag?.pointerId) return;
        this.scrollToBarPointer(event.clientX);
      }

      onBarPointerUp(event) {
        if (event.pointerId !== this.barDrag?.pointerId) return;

        this.barDrag = null;
        this.bar.releasePointerCapture?.(event.pointerId);
        this.bar.classList.remove('is-dragging');
        /* Snap returns and settles the track onto the nearest slide. */
        this.track.style.scrollSnapType = '';
      }

      /* The thumb travels (bar width - thumb width) for the track's full scroll
         range — the same mapping update() draws it with. */
      scrollToBarPointer(clientX) {
        const { barLeft, travel, grab } = this.barDrag;
        const ratio = travel > 0 ? Math.min(Math.max((clientX - barLeft - grab) / travel, 0), 1) : 0;
        this.track.scrollLeft = ratio * (this.track.scrollWidth - this.track.clientWidth);
      }

      /* ----- indicators ----- */

      onScroll() {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }

      onResize() {
        this.update();
      }

      /* The arrows move a whole number of slides and land on an exact snap
         point.

         Scrolling by a raw clientWidth is what broke this on iOS. The teams
         carousel shows 2.5 slides per view with no gap, so one viewport is
         exactly 2.5 steps and every press finished dead centre between two
         snap points, 68.6px from each. Which way scroll-snap-type: mandatory
         resolves that tie is implementation defined: Safari and Chrome pick
         differently, and the track settled half a slide out with the first
         slide showing only its right edge. A multiple of the step leaves
         nothing to resolve. */
      scrollPage(event) {
        const direction = event.currentTarget === this.prevButton ? -1 : 1;
        const step = this.slideStep();
        if (step <= 0) return;

        const max = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);

        /* Whole slides per press, at least one: a fractional per-view (2.5)
           must not advance by a fraction. */
        const perPage = Math.max(1, Math.floor(this.track.clientWidth / step));
        const index = Math.round(this.track.scrollLeft / step);
        let target = (index + direction * perPage) * step;

        /* The end of the track is rarely a multiple of the step and is a
           legitimate resting place; snapping back to the last whole slide
           would leave the final slide permanently half off screen. */
        if (target > max - 1) target = max;
        if (target < 0) target = 0;

        this.scrollToOffset(target);
      }

      /* iOS Safari handles a programmatic smooth scroll on a mandatory-snap
         track badly: the animation and the snap adjustment fight, and the
         scroll lands short or bounces back to where it started. That is the
         "arrows do nothing" behaviour reported for team-feature, which shows
         one slide per view and so has no tie to blame. Snapping is switched
         off for the duration of the animation and restored once the track
         settles; the target is already an exact snap point, so restoring it
         moves nothing. */
      scrollToOffset(target) {
        const track = this.track;
        cancelAnimationFrame(this.settleFrame);
        clearTimeout(this.settleTimer);

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.style.scrollSnapType = 'none';
        track.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });

        const restore = () => {
          cancelAnimationFrame(this.settleFrame);
          clearTimeout(this.settleTimer);
          track.style.scrollSnapType = '';
          this.update();
        };

        /* Settled when the position stops moving, or when it has arrived.
           scrollend would be tidier but Safari only shipped it in 17.4. */
        let last = track.scrollLeft;
        let stillFrames = 0;
        let frames = 0;
        const watch = () => {
          const now = track.scrollLeft;
          frames += 1;
          stillFrames = Math.abs(now - last) <= 0.5 ? stillFrames + 1 : 0;
          last = now;
          /* The grace period matters: a smooth scroll does not necessarily
             move on the first frames after the call, and without it the
             "stopped moving" test fires before the animation has begun and
             hands snapping back mid-flight - the very thing being avoided. */
          if (Math.abs(now - target) <= 1 || (frames > 5 && stillFrames >= 3)) {
            restore();
            return;
          }
          this.settleFrame = requestAnimationFrame(watch);
        };
        this.settleFrame = requestAnimationFrame(watch);
        /* Hard stop, so snapping can never be left switched off. */
        this.settleTimer = setTimeout(restore, 1000);
      }

      /* A dot travels in whatever unit it is drawn to represent, which update()
         decides: one dot per slide when looping, one per page otherwise.

         The non-looping case is driven from the scroll ratio rather than from
         `index * clientWidth`, because the ratio is how update() reads the
         active dot back. A viewport-sized step lands short whenever the last
         page is partial — 6 teams at 2.5 per view travel 1.4 viewports across
         3 dots — so clicking the last dot would never light it.

         Restored during the Main merge: Main binds this in the constructor and
         removes the listener on disconnect, but carries neither the method nor
         the `addEventListener`. A missing method makes `.bind()` throw, which
         fails the whole custom-element upgrade — no drag, no arrows, no dots,
         on every carousel including the cart drawer's two. */
      onDotClick(event) {
        const index = this.dots.indexOf(event.currentTarget);
        if (index < 0) return;

        if (this.loops || this.everySlideReachesStart) {
          const step = this.slideStep();
          if (step <= 0) return;
          this.scrollToOffset(index * step);
          return;
        }

        const { scrollWidth, clientWidth } = this.track;
        const max = Math.max(scrollWidth - clientWidth, 0);
        const pages = clientWidth > 0 ? Math.max(Math.ceil(scrollWidth / clientWidth), 1) : 1;

        this.scrollToOffset(pages > 1 ? (max * index) / (pages - 1) : 0);
      }

      onRefresh(event) {
        if (event.detail?.reset) {
          this.resetScroll();
          return;
        }
        this.update();
      }

      /* Safari remembers the slide it last snapped to and restores the scroll
         position to it after a layout change. When a filter hides that slide
         the restore has nothing sensible to aim at and the track comes back at
         an arbitrary offset, which is why changing filters and then using the
         arrows was the sequence that broke it. Taking snapping off for the
         reset removes the remembered target.

         The write also has to be instant. The track no longer sets
         scroll-behavior in CSS, so this is belt and braces rather than a
         fix now, but it is kept deliberately: a scrollLeft assignment does
         obey scroll-behavior, so if the declaration were ever put back the
         reset would silently start animating into the re-layout again. */
      resetScroll() {
        const track = this.track;
        if (!track) return;

        cancelAnimationFrame(this.settleFrame);
        clearTimeout(this.settleTimer);

        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = 0;
        /* Reading layout commits the write before snapping is allowed back. */
        void track.offsetWidth;

        this.scheduleRestore(track);
        this.update();
      }

      /* Puts the track back to snapping and smooth scrolling, on whichever of
         the frame or the timer arrives first, and only once.

         requestAnimationFrame on its own was not enough. A hidden or
         backgrounded tab can stop being served frames while timers keep
         running, and the restore then never happens: the track is left with
         scroll-snap-type: none and scroll-behavior: auto inline, so it stops
         snapping for the rest of the page\u2019s life. Verified against exactly
         that state, where the settle timer fired but no frame ever did. */
      scheduleRestore(track) {
        cancelAnimationFrame(this.restoreFrame);
        clearTimeout(this.restoreTimer);

        const restore = () => {
          cancelAnimationFrame(this.restoreFrame);
          clearTimeout(this.restoreTimer);
          track.style.scrollSnapType = '';
          track.style.scrollBehavior = '';
          this.update();
        };

        this.restoreFrame = requestAnimationFrame(restore);
        this.restoreTimer = setTimeout(restore, 100);
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const scrollable = Math.max(scrollWidth - clientWidth, 0);
        const ratio = scrollable > 0 ? scrollLeft / scrollable : 0;

        /* The whole trick of the seamless loop. Once the track has travelled a
           full set, what the viewport shows is clones that are pixel-identical to
           the originals one set back — so moving the scroll position back by
           exactly that distance changes nothing on screen, and the track is free
           to keep going forward for ever.
           Not while dragging: the wrap would fight the scrollLeft the drag writes.
           'instant' rather than 'auto', because 'auto' defers to the stylesheet,
           which sets scroll-behavior: smooth on the track — the jump would
           animate, and the animation is the very thing being hidden. */
        if (this.loops && !this.dragging) {
          const width = this.loopWidth;

          if (width > 0 && scrollLeft >= width - 1) {
            /* Direct assignment: scrollTo({behavior:'instant'}) is fine when
               idle, but a marquee tick writes scrollLeft on the next frame and
               the two fight if this goes through the scrolling API. */
            this.track.scrollLeft = scrollLeft - width;
            return;
          }
        }

        /* Only advertise grab-ability when there is somewhere to go. */
        this.track.classList.toggle('is-scrollable', scrollable > 1);

        if (this.thumb) {
          const visibleFraction = scrollWidth > 0 ? Math.min(clientWidth / scrollWidth, 1) : 1;
          this.style.setProperty('--carousel-thumb-width', `${visibleFraction * 100}%`);
          /* Just the 0..1 position. The CSS turns it into a distance using the
             bar's own width, which is the only width the thumb can travel in. */
          this.style.setProperty('--carousel-thumb-ratio', `${ratio}`);
        }

        const total = this.total;
        if (this.totalOutput) this.totalOutput.textContent = total;

        if (this.shown) {
          const step = this.slideStep();
          const visible = step > 0 ? Math.ceil((scrollLeft + clientWidth) / step) : total;
          this.shown.textContent = total === 0 ? 0 : Math.max(1, Math.min(visible, total));
        }

        if (this.dots.length) {
          let pages;
          let page;

          if (this.loops) {
            /* A looping track reaches every slide in turn, so one dot per slide,
               lit by whichever slide is at the left edge. Taken modulo the set so
               a position inside the clones reports the original it duplicates. */
            const width = this.loopWidth;
            const step = this.slideStep();

            pages = this.dots.length;
            page = width > 0 && step > 0 ? Math.round((scrollLeft % width) / step) % pages : 0;
          } else if (this.everySlideReachesStart) {
            const step = this.slideStep();
            pages = this.dots.length;
            page = Math.min(Math.round(scrollLeft / step), pages - 1);
          } else {
            /* One dot per scrollable page, not per slide — with several slides in
               view, slide-count dots would outnumber the positions reachable. */
            pages = clientWidth > 0 ? Math.max(Math.ceil(scrollWidth / clientWidth), 1) : 1;

            /* Map scroll progress onto the dots. Dividing scrollLeft by a viewport
               instead never lights the last dot: the track only travels
               (scrollWidth - clientWidth), which is short of pages × clientWidth
               whenever the last page is partial — 6 teams at 2.5 per view travel
               1.4 viewports across 3 dots, so that quotient stops at 1. Driving it
               from the ratio guarantees dot 0 at the start and the final dot at
               the end. */
            page = Math.round(ratio * (pages - 1));
          }

          for (const [index, dot] of this.dots.entries()) {
            dot.toggleAttribute('hidden', index >= pages);
            dot.toggleAttribute('data-active', index === page);
            /* data-active is the styling hook; aria-current is what a screen
               reader reads, now that the dots are real controls. */
            if (index === page) {
              dot.setAttribute('aria-current', 'true');
            } else {
              dot.removeAttribute('aria-current');
            }
          }
        }

        if (this.prevButton) this.prevButton.disabled = scrollLeft <= 1;
        if (this.nextButton) this.nextButton.disabled = scrollLeft >= scrollable - 1;
      }

      /* Distance from the start of one slide to the start of the next, gap included. */
      slideStep() {
        const [first, second] = this.visibleSlides;
        if (!first) return 0;
        if (second) return second.offsetLeft - first.offsetLeft;
        return first.offsetWidth;
      }
    },
  );
}

/**
 * Marquee extras — dots, drag, and keeping the active dot in sync.
 *
 * Drift and hover-pause are CSS (animation + :hover on the photo strip) so they
 * do not depend on this module having rebound after a hot reload. These
 * listeners sit on `document` for the same reason.
 */
function marqueeRoot(node) {
  return node?.closest?.('scroll-carousel.scroll-carousel--marquee') || null;
}

function marqueeDuration(root, strip) {
  const fromStyle = parseFloat(root.style.getPropertyValue('--carousel-marquee-duration'));
  if (fromStyle > 0) return fromStyle;
  return parseFloat(getComputedStyle(strip).animationDuration) || 0;
}

function marqueeOffset(strip) {
  const matrix = new DOMMatrix(getComputedStyle(strip).transform);
  return -matrix.m41;
}

function marqueeStep(track) {
  const slides = track.children;
  if (!slides.length) return 0;
  if (slides.length > 1) return slides[1].offsetLeft - slides[0].offsetLeft;
  return slides[0].offsetWidth;
}

function syncMarqueeDots(root, offset) {
  const track = root.querySelector('[data-carousel-track]');
  const dots = root.querySelectorAll('.scroll-carousel__dot');
  if (!track || !dots.length) return;

  const width = track.offsetWidth;
  const step = marqueeStep(track);
  const wrapped = width > 0 ? ((offset % width) + width) % width : 0;
  const page = step > 0 ? Math.round(wrapped / step) % dots.length : 0;

  for (const [index, dot] of dots.entries()) {
    dot.toggleAttribute('data-active', index === page);
    if (index === page) {
      dot.setAttribute('aria-current', 'true');
    } else {
      dot.removeAttribute('aria-current');
    }
  }
}

/** ms the strip holds still after a swipe before it picks the glide back up */
const MARQUEE_HOLD_MS = 3000;

/* Touch-width only. On a pointer device the strip drifting straight on from
   where the cursor let go is the intended behaviour; a swipe is a discrete
   gesture and wants to land on something. */
function marqueeSettlesOnRelease() {
  return window.matchMedia('(max-width: 749px)').matches;
}

/**
 * The offset that puts the nearest slide's centre on the viewport's centre.
 * On mobile a slide is the full width of the box, so this is also the slide
 * boundary — the bias term is what keeps it honest if that ever changes.
 */
function marqueeCentreOffset(root, track, offset) {
  const step = marqueeStep(track);
  const slide = track.children[0];
  const viewport = root.querySelector('.scroll-carousel__marquee-viewport');
  if (step <= 0 || !slide || !viewport) return offset;

  const bias = slide.offsetWidth / 2 - viewport.clientWidth / 2;
  return Math.round((offset - bias) / step) * step + bias;
}

const marqueeHolds = new WeakMap();

function holdMarquee(root) {
  clearTimeout(marqueeHolds.get(root));
  root.classList.add('is-marquee-held');

  marqueeHolds.set(
    root,
    setTimeout(() => {
      root.classList.remove('is-marquee-held');
      marqueeHolds.delete(root);
    }, MARQUEE_HOLD_MS)
  );
}

function seekMarquee(root, offset) {
  const strip = root.querySelector('.scroll-carousel__marquee-strip');
  const track = root.querySelector('[data-carousel-track]');
  if (!strip || !track) return;

  const width = track.offsetWidth;
  const wrapped = width > 0 ? ((offset % width) + width) % width : 0;
  const duration = marqueeDuration(root, strip);
  const delay = width > 0 && duration > 0 ? -(wrapped / width) * duration : 0;

  strip.style.animation = 'none';
  strip.style.transform = `translate3d(${-wrapped}px, 0, 0)`;
  void strip.offsetWidth;
  strip.style.transform = '';
  strip.style.animation = '';
  strip.style.animationDelay = `${delay}s`;
  syncMarqueeDots(root, wrapped);
}

document.addEventListener(
  'click',
  (event) => {
    const dot = event.target.closest('.scroll-carousel--marquee .scroll-carousel__dot');
    if (!dot) return;

    const root = marqueeRoot(dot);
    const track = root?.querySelector('[data-carousel-track]');
    if (!root || !track) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const index = Array.from(root.querySelectorAll('.scroll-carousel__dot')).indexOf(dot);
    seekMarquee(root, index * marqueeStep(track));

    /* A mouse click focuses the button, which would keep :has(:focus) paused.
       Keyboard activation (detail === 0) leaves focus so the pause stays. */
    if (event.detail > 0) dot.blur();
  },
  true,
);

let marqueeDrag = null;

document.addEventListener('pointerdown', (event) => {
  const viewport = event.target.closest('.scroll-carousel--marquee .scroll-carousel__marquee-viewport');
  if (!viewport) return;
  if (event.pointerType !== 'touch' && event.button !== 0) return;
  /* The PDP card sits on the gallery. A hit here is for the form, not a drag.
     setPointerCapture is also skipped: document already hears move/up, and a
     capture that never released ate every later click — including Add to cart. */
  if (event.target.closest('.pdp-hero__card-slot')) return;
  const card = document.querySelector('.pdp-hero__card-slot');
  if (card) {
    const box = card.getBoundingClientRect();
    if (
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom
    ) {
      return;
    }
  }

  const root = marqueeRoot(viewport);
  const strip = root?.querySelector('.scroll-carousel__marquee-strip');
  if (!root || !strip) return;

  marqueeDrag = {
    root,
    strip,
    originX: event.clientX,
    originOffset: marqueeOffset(strip),
  };

  root.classList.add('is-marquee-paused');
  strip.classList.add('is-dragging');
  strip.style.animation = 'none';
  strip.style.transform = `translate3d(${-marqueeDrag.originOffset}px, 0, 0)`;
});

document.addEventListener('pointermove', (event) => {
  if (!marqueeDrag) return;
  const offset = marqueeDrag.originOffset - (event.clientX - marqueeDrag.originX);
  marqueeDrag.strip.style.transform = `translate3d(${-offset}px, 0, 0)`;
  syncMarqueeDots(marqueeDrag.root, offset);
});

function endMarqueeDrag(event) {
  if (!marqueeDrag) return;
  const { root, strip, originX, originOffset } = marqueeDrag;
  const offset = originOffset - (event.clientX - originX);
  const track = root.querySelector('[data-carousel-track]');
  strip.classList.remove('is-dragging');
  root.classList.remove('is-marquee-paused');
  marqueeDrag = null;

  /* A swipe settles: the nearest slide lands centred, the strip sits still for
     MARQUEE_HOLD_MS so it can be looked at, then the glide resumes from there. */
  if (track && marqueeSettlesOnRelease()) {
    seekMarquee(root, marqueeCentreOffset(root, track, offset));
    holdMarquee(root);
    return;
  }

  seekMarquee(root, offset);
}

document.addEventListener('pointerup', endMarqueeDrag);
document.addEventListener('pointercancel', endMarqueeDrag);

document.addEventListener('dragstart', (event) => {
  if (event.target.closest('.scroll-carousel--marquee')) event.preventDefault();
});

document.addEventListener('carousel:seek', (event) => {
  const root = marqueeRoot(event.target);
  if (root && event.detail?.offset != null) seekMarquee(root, event.detail.offset);
});

function tickMarqueeDots() {
  if (!marqueeDrag) {
    for (const root of document.querySelectorAll('scroll-carousel.scroll-carousel--marquee')) {
      const strip = root.querySelector('.scroll-carousel__marquee-strip');
      if (strip) syncMarqueeDots(root, marqueeOffset(strip));
    }
  }
  requestAnimationFrame(tickMarqueeDots);
}

requestAnimationFrame(tickMarqueeDots);
